import styled, { keyframes } from 'styled-components';

import { CALIBRATE_ANIM } from '../../../../theme/tokens';

/**
 * ONE shared focal rack for the whole field. Every particle (and the icon's ring, which runs
 * the matching keyframe) is on THIS timeline, so the scene racks focus together rather than
 * each element drifting on its own clock. A particle is crisp (`--blur-min`) during the dwell
 * at the cycle ends and softest (`--blur-max`) in the middle. A per-particle NEGATIVE
 * animation-delay (its "depth") staggers when each one peaks, so the focal plane sweeps
 * through the field (near dots snap first, far blobs later) instead of every dot snapping at
 * once. Depths are biased toward 0, so the bulk of the field hits its in-focus beat together
 * with the ring (delay 0), then the sweep carries the rest.
 */
const focusRack = keyframes`
  0%, 12% { filter: blur(var(--blur-min)); }
  50% { filter: blur(var(--blur-max)); }
  88%, 100% { filter: blur(var(--blur-min)); }
`;

/** Slow parallax drift, per-particle vector via CSS custom props. */
const drift = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(var(--bx), var(--by), 0); }
`;

/** A bokeh layer fills the whole panel and screen-blends its particles (so they add up like
 *  real out-of-focus light). On light theme it multiplies so the dots still tint. */
export const BokehLayer = styled.div<{ $z: number }>`
  position: absolute;
  inset: 0;
  z-index: ${(p) => p.$z};
  overflow: hidden;
  pointer-events: none;
  mix-blend-mode: screen;
  isolation: isolate;
  opacity:0.5;

  :root[data-ows-theme='light'] & {
    mix-blend-mode: multiply;
    opacity: 0.7;
  }
`;

export const Particle = styled.span<{
  $top: string;
  $left: string;
  $size: number;
  $color: string;
  $opacity: number;
  $blurMin: number;
  $blurMax: number;
  $focusDelay: number;
  $bx: string;
  $by: string;
  $driftDur: number;
  $driftDelay: number;
}>`
  position: absolute;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  background: radial-gradient(closest-side, ${(p) => p.$color}, transparent 72%);
  opacity: ${(p) => p.$opacity};
  --blur-min: ${(p) => p.$blurMin}px;
  --blur-max: ${(p) => p.$blurMax}px;
  --bx: ${(p) => p.$bx};
  --by: ${(p) => p.$by};
  will-change: transform, filter;
  /* focus rack (shared clock; the per-particle delay = its depth) + slow drift. */
  animation: ${focusRack} ${CALIBRATE_ANIM.focusHuntMs}ms ${CALIBRATE_ANIM.ease} infinite,
    ${drift} ${(p) => p.$driftDur}ms ease-in-out infinite;
  animation-delay: -${(p) => p.$focusDelay}ms, ${(p) => p.$driftDelay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: none;
    filter: blur(var(--blur-min));
  }
`;

/** A big foreground depth disc anchored to a SIDE. Unlike the scattered particles it is sized
 *  as a percentage of the panel WIDTH (so it scales with the panel) and pushed mostly off-frame
 *  via a side offset, leaving only its inner arc bleeding in to frame the edge. Square via
 *  `aspect-ratio`. Shares the focus rack + drift with the rest of the field. */
export const EdgeDisc = styled.span<{
  $side: 'left' | 'right';
  $offset: string;
  $top: string;
  $size: string;
  $color: string;
  $opacity: number;
  $blurMin: number;
  $blurMax: number;
  $focusDelay: number;
  $bx: string;
  $by: string;
  $driftDur: number;
  $driftDelay: number;
}>`
  position: absolute;
  top: ${(p) => p.$top};
  ${(p) => `${p.$side}: ${p.$offset};`}
  width: ${(p) => p.$size};
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(closest-side, ${(p) => p.$color}, transparent 72%);
  opacity: ${(p) => p.$opacity};
  --blur-min: ${(p) => p.$blurMin}px;
  --blur-max: ${(p) => p.$blurMax}px;
  --bx: ${(p) => p.$bx};
  --by: ${(p) => p.$by};
  will-change: transform, filter;
  animation: ${focusRack} ${CALIBRATE_ANIM.focusHuntMs}ms ${CALIBRATE_ANIM.ease} infinite,
    ${drift} ${(p) => p.$driftDur}ms ease-in-out infinite;
  animation-delay: -${(p) => p.$focusDelay}ms, ${(p) => p.$driftDelay}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: none;
    filter: blur(var(--blur-min));
  }
`;
